import { hostState, record, refusal, trustedOrigin, type HostProvider } from './host-state.js';

const key = 'tasks:vitto:pkce:v1';
const b64 = /^[A-Za-z0-9_-]{43}$/;
function encode(bytes: Uint8Array) {
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}
export interface StandaloneOptions {
  vittoOrigin: string;
  apiOrigin: string;
  appName: string;
}
/**
 * What a NON-200 redeem means for the person (CONTRATO-1b2-WIRE §3.2: «any other answer ⇒ nothing was delivered, the app stays
 * signed out» — the message is the app's). 400/401/403: the code or the person's Vitto session is no good ⇒ sign in again;
 * 429: Vitto's own limit ⇒ wait; anything else (5xx, an unexpected status): Vitto cannot answer now ⇒ unavailable.
 */
const failure = (status: number) =>
  status === 400 || status === 401 || status === 403
    ? 'session_invalid'
    : status === 429
      ? 'rate_limited'
      : 'unavailable';
export function standaloneHost(
  win: Window,
  options: StandaloneOptions,
  fetcher: typeof fetch = fetch,
): HostProvider {
  const state = hostState();
  const valid =
    trustedOrigin(options.vittoOrigin) && trustedOrigin(options.apiOrigin) && !!options.appName;
  let inFlight = false;
  let attempted = false;
  let connecting = false;
  async function redeem(): Promise<string> {
    if (attempted) throw new Error('session_invalid');
    attempted = true;
    const params = new URLSearchParams(win.location.hash.slice(1));
    // Strip return material even when validation fails; never leave a code in navigation history.
    if (win.location.hash)
      win.history.replaceState(win.history.state, '', win.location.pathname + win.location.search);
    const raw = win.sessionStorage.getItem(key);
    win.sessionStorage.removeItem(key);
    let saved: unknown;
    try {
      saved = JSON.parse(raw ?? 'null');
    } catch {
      throw new Error('session_invalid');
    }
    const code = params.get('code') ?? '';
    if (
      !valid ||
      params.get('vitto-return') !== 'code' ||
      !b64.test(code) ||
      !record(saved) ||
      saved.state !== params.get('state') ||
      typeof saved.state !== 'string' ||
      !b64.test(saved.state) ||
      typeof saved.verifier !== 'string' ||
      !/^[A-Za-z0-9_-]{43,128}$/.test(saved.verifier) ||
      saved.vittoOrigin !== options.vittoOrigin ||
      saved.appOrigin !== win.location.origin ||
      typeof saved.created !== 'number' ||
      !Number.isFinite(saved.created) ||
      Date.now() < saved.created ||
      Date.now() - saved.created >= 600000
    )
      throw new Error('session_invalid');
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      return await Promise.race([
        (async () => {
          const response = await fetcher(`${options.apiOrigin}/api/auth/app-return/redeem`, {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            credentials: 'omit',
            redirect: 'error',
            body: JSON.stringify({ code, verifier: saved.verifier }),
            signal: controller.signal,
          });
          if (!response.ok) throw new Error(failure(response.status));
          const data: unknown = await response.json();
          if (!record(data) || typeof data.assertion !== 'string') throw new Error('unavailable');
          if (
            data.grant_half === 'grant' &&
            typeof data.app_grant === 'string' &&
            data.app_grant &&
            !('app_grant_denied' in data)
          )
            return data.app_grant;
          if (
            data.grant_half === 'denied' &&
            !('app_grant' in data) &&
            record(data.app_grant_denied)
          ) {
            const reason = refusal(data.app_grant_denied.reason);
            throw new Error(reason === 'busy' || reason === 'duplicate' ? 'unavailable' : reason);
          }
          throw new Error('unavailable');
        })(),
        new Promise<never>((_, reject) => {
          timer = setTimeout(() => {
            controller.abort();
            reject(new Error('timeout'));
          }, 15000);
        }),
      ]);
    } finally {
      clearTimeout(timer);
    }
  }
  return {
    getState: state.getState,
    subscribe: state.subscribe,
    async grants(reason) {
      if (inFlight) throw new Error('busy');
      if (reason === 'renew') {
        state.set('renew_needs_gesture');
        throw new Error('renew_needs_gesture');
      }
      inFlight = true;
      state.set('waiting_init');
      try {
        const grant = await redeem();
        state.set('ready');
        return grant;
      } catch (error) {
        const message = error instanceof Error ? error.message : '';
        const normalized = message === 'timeout' ? 'timeout' : refusal(message);
        state.set(normalized === 'timeout' ? 'timeout' : `refused:${normalized}`);
        throw new Error(normalized);
      } finally {
        inFlight = false;
      }
    },
    async connect() {
      if (!valid || connecting) return;
      // This is the only redirect path; the UI invokes it after explicit confirmation.
      connecting = true;
      try {
        const verifier = encode(win.crypto.getRandomValues(new Uint8Array(32)));
        const nonce = encode(win.crypto.getRandomValues(new Uint8Array(32)));
        const challenge = encode(
          new Uint8Array(
            await win.crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier)),
          ),
        );
        win.sessionStorage.setItem(
          key,
          JSON.stringify({
            verifier,
            state: nonce,
            created: Date.now(),
            vittoOrigin: options.vittoOrigin,
            appOrigin: win.location.origin,
          }),
        );
        const url = new URL('/authorize', options.vittoOrigin);
        url.search = new URLSearchParams({
          app: options.appName,
          origin: win.location.origin,
          delivery: 'redirect',
          state: nonce,
          challenge,
        }).toString();
        win.location.assign(url.href);
      } catch {
        connecting = false;
        state.set('refused:unavailable');
      }
    },
  };
}
